import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { useBranchStore } from '@/store/branchStore';
import {
  capitalizeFirstLetter,
  DAYS_ORDER,
  formatTime,
} from '@/utils/dateTime';
import {
  AiPhone01Icon,
  Clock01Icon,
  MapPinIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useEffect, useState } from 'react';

const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1739289696449-cba3a5ef085d?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1671108503276-1d3d5ab23a3a?q=80&w=2232&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?q=80&w=2079&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
];

export default function AboutPage() {
  const [carouselIndex, setCarouselIndex] = useState(0);

  const branchInfo = useBranchStore(s => s.branchInfo);
  const storefrontChannel = useBranchStore(s => s.storefrontChannel);
  const getHeadline = useBranchStore(s => s.getHeadline);
  const getMissionStatement = useBranchStore(s => s.getMissionStatement);

  const premiseImages = storefrontChannel?.premiseImages || [];
  const pharmacyImages =
    premiseImages.length > 0 ? premiseImages : FALLBACK_IMAGES;

  const headline =
    getHeadline() ||
    'Committed to providing quality healthcare services and medications to our community for over 20 years.';
  const missionStatement =
    getMissionStatement() ||
    'At our pharmacy, we believe that everyone deserves access to quality healthcare and medications. Our mission is to provide comprehensive pharmaceutical services, expert advice, and personalized care to help our patients achieve optimal health outcomes. We are committed to maintaining the highest standards of safety, efficacy, and customer service in everything we do.';

  const address = branchInfo
    ? `${branchInfo.address}, ${branchInfo.lga}, ${branchInfo.state}`
    : '123 Health Street Victoria Island, Lagos, Nigeria';
  const phoneNumber = branchInfo
    ? `${branchInfo.dial_code}${branchInfo.phone_number}`
    : '+234 901 234 5678';

  const handleGetDirections = () => {
    const encodedAddress = encodeURIComponent(address);
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
      '_blank'
    );
  };

  const handleContactUs = () => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const workHoursMap = new Map<string, { open: string; close: string }[]>();

  if (branchInfo?.workHours) {
    branchInfo.workHours.forEach(
      (wh: { day: string; open: string; close: string }) => {
        const day = wh.day.toLowerCase();
        const openTimes = wh.open.split(',').map(t => t.trim());
        const closeTimes = wh.close.split(',').map(t => t.trim());

        const hours = openTimes.map((open, idx) => ({
          open,
          close: closeTimes[idx] || closeTimes[0],
        }));

        workHoursMap.set(day, hours);
      }
    );
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndex(prevIndex => (prevIndex + 1) % pharmacyImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [pharmacyImages.length]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header showSearchInHeader={false} />

      <section className="w-full bg-white">
        <div className="container py-0">
          <div className="text-center pt-6 pb-10 md:pt-12 md:pb-16">
            <h1 className="text-4xl mb-4 text-primary">About Our Pharmacy</h1>
            <p className="bodysmall md:bodylg text-[var(--dispensary-gray-600)] max-w-2xl mx-auto">
              {headline}
            </p>
          </div>

          <div className="w-full h-[300px] md:h-[520px] mb-12 overflow-hidden relative mx-auto">
            <img
              src={pharmacyImages[carouselIndex]}
              alt="Pharmacy"
              className="w-full h-full object-cover transition-all duration-300 ease-out-cubic rounded-xl shadow-md bg-gray-200"
            />
            <div className="absolute left-1/2 -translate-x-1/2 bottom-4 flex gap-2 z-10">
              {pharmacyImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCarouselIndex(idx)}
                  className={`w-2 h-2 rounded-full border border-white shadow ${
                    carouselIndex === idx ? 'bg-white' : 'bg-white/40'
                  } transition-all`}
                  aria-label={`Go to slide ${idx + 1}`}
                  style={{ width: 8, height: 8 }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <main className="flex-1 bg-[var(--theme-bg-subtle)]">
        <div className="container mx-auto py-6 md:py-10 bg-primary-bg">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2 space-y-8">
              <div>
                <div>
                  <h5 className="h5 font-semibold text-primary">Our Mission</h5>
                </div>
                <div>
                  <p className="bodymedium text-[var(--dispensary-gray-600)] leading-relaxed mt-4">
                    {missionStatement}
                  </p>
                </div>
              </div>

              <div>
                <div>
                  <h5 className="h5 font-semibold text-primary">Our Team</h5>
                </div>
                <div>
                  <p className="bodymedium text-[var(--dispensary-gray-600)] mt-4 mb-4">
                    Our experienced team of licensed pharmacists and healthcare
                    professionals are dedicated to providing you with the best
                    possible care and service.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <div>
                  <h5 className="h5 flex items-center gap-2 font-semibold text-primary">
                    Location & Hours
                  </h5>

                  <div className="mt-4">
                    <div className="flex flex-col gap-4 mb-6">
                      <div>
                        <p className="mb-2 flex items-center gap-2 text-primary">
                          <HugeiconsIcon icon={MapPinIcon} size={20} />
                          Address
                        </p>
                        <p className="bodysmall text-[var(--dispensary-gray-600)]">
                          {address}
                        </p>
                      </div>
                      <div>
                        <p className="mb-2 flex items-center gap-2 text-primary">
                          <HugeiconsIcon icon={Clock01Icon} size={20} />
                          Operating Hours
                        </p>
                        <div className="bodysmall text-[var(--dispensary-gray-600)] space-y-1">
                          {DAYS_ORDER.map(day => {
                            const hours = workHoursMap.get(day);
                            if (!hours) {
                              return (
                                <div key={day} className="flex justify-between">
                                  <span>{capitalizeFirstLetter(day)}</span>
                                  <span>Closed</span>
                                </div>
                              );
                            }
                            return (
                              <div key={day} className="flex justify-between">
                                <span>{capitalizeFirstLetter(day)}</span>
                                <span>
                                  {hours.map((h, idx) => (
                                    <span key={idx}>
                                      {formatTime(h.open)} -{' '}
                                      {formatTime(h.close)}
                                      {idx < hours.length - 1 && ', '}
                                    </span>
                                  ))}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={handleGetDirections}
                      className="w-full hover:bg-[var(--button-hover)]"
                    >
                      <HugeiconsIcon
                        icon={MapPinIcon}
                        size={16}
                        className="mr-2"
                      />
                      Get Directions
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <div>
                  <h5 className="h5 font-semibold text-primary">
                    Contact Information
                  </h5>
                </div>
                <div className="space-y-3 mt-4">
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon icon={AiPhone01Icon} size={16} />
                    <a
                      href={`tel:${phoneNumber}`}
                      className="bodysmall text-[var(--dispensary-gray-600)] hover:underline"
                    >
                      {phoneNumber}
                    </a>
                  </div>

                  <Button
                    onClick={handleContactUs}
                    variant="outline"
                    className="w-full mt-4 bg-white hover:bg-[var(--dispensary-gray-100)]"
                  >
                    Contact Us
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
